import { type DbClient, useLiveQuery } from "@tanstack/react-db";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { Button } from "@wags/ui/button.tsx";
import { Header } from "@/components/Header";
import { NotFoundState } from "@/components/shared/NotFound";
import { BREED_BACK_LINK_CLASS, BreedPageBody } from "./breed-page-body";
import { BreedPageSkeleton } from "./breed-page-skeleton";
import { breedPageSurface } from "./breed-page-surface";
import { breedsLiveQueryOptions, preloadBreeds } from "./breeds";
import { breedPageHead } from "./route-heads";

export function BreedPage() {
    const { breedId } = useParams({ from: "/breed/$breedId" });
    const search = useSearch({ from: "__root__" });
    const {
        data: breeds,
        isLoading,
        isIdle,
    } = useLiveQuery(breedsLiveQueryOptions);
    const surface = breedPageSurface({
        breeds,
        breedId,
        isLoading: isLoading || isIdle,
    });

    if (surface.state === "loading") {
        return <BreedPageSkeleton />;
    }

    if (surface.state === "missing") {
        return (
            <>
                <Header className="bg-true-white" />
                <div className="min-h-dvh bg-true-white pt-[70px]">
                    <NotFoundState
                        title="This breed page isn't here"
                        description="The page may have moved, or the link may be incorrect."
                        actions={
                            <Button asChild size="lg">
                                <Link to="/find" search={search}>
                                    Browse dogs
                                </Link>
                            </Button>
                        }
                    />
                </div>
            </>
        );
    }

    const { breed } = surface;

    return (
        <>
            <Header className="bg-true-white" />
            <div className="min-h-dvh w-full bg-maltese-white pt-[93px]">
                <BreedPageBody
                    breedName={breed.name}
                    backLink={
                        <Link
                            to="/find"
                            search={search}
                            className={BREED_BACK_LINK_CLASS}
                        >
                            Back to browse
                        </Link>
                    }
                    searchButton={
                        <Button asChild className="w-fit">
                            <Link
                                to="/find"
                                search={{
                                    ...search,
                                    breedSlugs: [breed.slug],
                                    feedContext: breed.name,
                                    page: 0,
                                    limit: 50,
                                }}
                            >
                                Search {breed.name}
                            </Link>
                        </Button>
                    }
                />
            </div>
        </>
    );
}

export const breedRouteOptions = {
    loader: async ({ context }: { context: { dbClient: DbClient } }) => {
        await preloadBreeds(context.dbClient);
    },
    pendingComponent: BreedPageSkeleton,
    head: ({ params }: { params: { breedId: string } }) => ({
        ...breedPageHead(params.breedId),
    }),
};
